import React, { useState, useRef, useEffect } from 'react';
import { 
   Dialog, 
   Box, 
   Typography, 
   Button, 
   TextField, 
   Select, 
   MenuItem, 
   FormControl,
   Fade,
   Backdrop
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const FeedbackTab = styled(Box)(({ theme }) => ({
   position: 'fixed',
   right: '-4px',
   top: '50%',
   transform: 'translateY(-50%)',
   zIndex: 1300,
   cursor: 'pointer',
   backgroundColor: '#E7F4FB',
   color: '#333333',
   fontWeight: 600,
   borderTopLeftRadius: '6px',
   borderBottomLeftRadius: '6px',
   boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
   width: '32px',
   height: '80px',
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'center',
   transition: 'all 0.3s ease',
   '&:hover': {
      backgroundColor: '#fed7aa',
   }
}));

const TabText = styled(Typography)({
   transform: 'rotate(-90deg)',
   fontSize: '14px',
   fontWeight: 500,
   whiteSpace: 'nowrap'
});

const RatingButton = styled(Button)(({ selected }) => ({
   width: '100%',
   padding: '12px',
   textAlign: 'left',
   justifyContent: 'flex-start',
   borderRadius: '6px',
   border: '1px solid #e5e7eb',
   backgroundColor: selected ? '#0040bf' : '#eff6ff',
   color: selected ? 'white' : '#374151',
   textTransform: 'none',
   marginBottom: '8px',
   '&:hover': {
      backgroundColor: selected ? '#2563eb' : '#f3f4f6',
   }
}));

const StyledSelect = styled(Select)(({ hasValue }) => ({
   backgroundColor: hasValue ? '#0040bf' : '#E7F4FB',
   color: hasValue ? 'white' : 'black',
   '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d1d5db',
   },
   '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3b82f6',
   },
   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3b82f6',
   },
   '& .MuiSelect-icon': {
      color: hasValue ? 'white' : 'inherit',
   }
}));

const FeedbackWidget = () => {
   const [isOpen, setIsOpen] = useState(false);
   const [selectedRating, setSelectedRating] = useState('');
   const [selectedFeature, setSelectedFeature] = useState('');
   const [feedbackText, setFeedbackText] = useState('');
   const [isSubmitted, setIsSubmitted] = useState(false);
   const textareaRef = useRef(null);
   const scrollContainerRef = useRef(null);

   const ratings = [
      { value: 'excellent', label: 'Excellent', emoji: '😊' },
      { value: 'good', label: 'Good', emoji: '😊' },
      { value: 'okay', label: 'Okay', emoji: '😐' },
      { value: 'disappointing', label: 'Disappointing', emoji: '😞' },
      { value: 'poor', label: 'Poor', emoji: '😢' }
   ];

   const features = [
      'DND/Non-DND filtering',
      'Messaging Reports & Analytics',
      'Message Scheduling',
      'Personalized Sender ID',
      'Customer Support',
      'Dashboard Interface',
      'Other'
   ];

   // Handle body scroll with useEffect
  useEffect(() => {
   const originalOverflow = document.body.style.overflow;
   const originalPaddingRight = document.body.style.paddingRight;

   if (isOpen) {
      document.body.style.overflow = 'hidden';
   } else {
      document.body.style.overflow = originalOverflow || '';
      document.body.style.paddingRight = originalPaddingRight || '';
   }

   return () => {
      document.body.style.overflow = originalOverflow || '';
      document.body.style.paddingRight = originalPaddingRight || '';
   };
}, [isOpen]);


   useEffect(() => {
      if (isOpen && textareaRef.current) {
         setTimeout(() => textareaRef.current?.focus(), 400);
      }
   }, [isOpen]);

   const handleOpen = () => {
      setIsOpen(true);
   };

   const handleClose = () => {
      setIsOpen(false);
      
      // Reset form after animation completes
      setTimeout(() => {
         setSelectedRating('');
         setSelectedFeature('');
         setFeedbackText('');
         setIsSubmitted(false);
      }, 300);
   };

   const handleRatingSelect = (ratingValue) => {
      setSelectedRating(ratingValue);
      
      setTimeout(() => {
         if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
               top: scrollContainerRef.current.scrollHeight,
               behavior: 'smooth'
            });
         }
      }, 100);
   };

   const handleSubmit = async () => {
      if (!selectedRating) {
         alert('Please select a rating before submitting.');
         return;
      }

      if (!selectedFeature) {
         alert('Please select a feature before submitting.');
         return;
      }

      const payload = {
         rating: selectedRating,
         feature: selectedFeature,
         message: feedbackText || null,
         timestamp: new Date().toISOString()
      };

      console.log('Feedback payload:', payload);

      try {
         await contactSupport(payload);
         setIsSubmitted(true);
      } catch (error) {
         console.error('Error sending feedback:', error);
         alert('Failed to send feedback. Please try again.');
      }
   };

   const contactSupport = async (payload) => {
      try {
         const response = await fetch('https://yu6mwcifjkdlwduqgvo22wqzti0dugax.lambda-url.eu-west-1.on.aws/v1/feature/contact-us', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
         });

         if (!response.ok) throw new Error('Failed to send feedback');
         console.log('Feedback sent successfully');
      } catch (error) {
         throw error;
      }
   };

   return (
      <>
         {/* Feedback Tab */}
         <FeedbackTab onClick={handleOpen}>
            <TabText >Feedback</TabText>
         </FeedbackTab>

         {/* Feedback Dialog */}
         <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
             disableScrollLock
            PaperProps={{
               sx: {
                  position: 'fixed',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  margin: 0,
                  maxHeight: '600px',
                  width: '320px',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
               }
            }}
            BackdropProps={{
               sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
         >
            {!isSubmitted ? (
               <>
                  {/* Header */}
                  <Box sx={{ 
                     bgcolor: '#0040bf', 
                     color: 'white', 
                     p: 2, 
                     display: 'flex', 
                     justifyContent: 'space-between', 
                     alignItems: 'center' 
                  }}>
                     <Typography variant="h6" fontWeight="medium">
                        Feedback
                     </Typography>
                     <Button 
                        onClick={handleClose}
                        sx={{ 
                           color: 'white', 
                           minWidth: 'auto', 
                           p: 0.5,
                           '&:hover': { color: '#e5e7eb' }
                        }}
                     >
                        ✕
                     </Button>
                  </Box>

                  {/* Content */}
                  <Box 
                     ref={scrollContainerRef}
                     sx={{ p: 2, bgcolor: 'white', maxHeight: '400px', overflowY: 'auto' }}
                  >
                     {/* Rating Section */}
                     <Box sx={{ mb: 3 }}>
                        <Typography 
                           variant="body2" 
                           color="#374151" 
                           fontWeight="medium" 
                           mb={2}
                        >
                           Please rate your overall experience with Parrotica today
                        </Typography>
                        <Box>
                           {ratings.map((rating) => (
                              <RatingButton
                                 key={rating.value}
                                 selected={selectedRating === rating.value}
                                 onClick={() => handleRatingSelect(rating.value)}
                              >
                                 <span style={{ marginRight: '8px', fontSize: '18px' }}>
                                    {rating.emoji}
                                 </span>
                                 {rating.label}
                              </RatingButton>
                           ))}
                        </Box>
                     </Box>

                     {/* Feature Selection */}
                     <Box sx={{ mb: 3 }}>
                        <Typography 
                           variant="body2" 
                           color="#374151" 
                           fontWeight="medium" 
                           mb={1}
                        >
                           Which feature does your feedback most relate to?
                        </Typography>
                        <FormControl fullWidth>
                           <StyledSelect
                              value={selectedFeature}
                              onChange={(e) => setSelectedFeature(e.target.value)}
                              displayEmpty
                              hasValue={!!selectedFeature}
                              size="small"
                           >
                              <MenuItem value="" disabled>
                                 Select a feature
                              </MenuItem>
                              {features.map((feature) => (
                                 <MenuItem key={feature} value={feature}>
                                    {feature}
                                 </MenuItem>
                              ))}
                           </StyledSelect>
                        </FormControl>
                     </Box>

                     {/* Additional Information */}
                     <Box sx={{ mb: 3 }}>
                        <Typography 
                           variant="body2" 
                           color="#374151" 
                           fontWeight="medium" 
                           mb={1}
                        >
                           Additional information (optional)
                        </Typography>
                        <TextField
                           ref={textareaRef}
                           multiline
                           rows={3}
                           fullWidth
                           value={feedbackText}
                           onChange={(e) => setFeedbackText(e.target.value)}
                           placeholder="Please share any additional details..."
                           sx={{
                              '& .MuiOutlinedInput-root': {
                                 fontSize: '14px',
                                 lineHeight: '20px',
                                 padding: '8px 12px',
                                 alignItems: 'flex-start',
                              },
                              '& .MuiInputBase-inputMultiline': {
                                 padding: 0,
                              },
                           }}
                        />
                     </Box>

                     {/* Submit Button */}
                     <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                           bgcolor: '#0040bf',
                           py: 1.5,
                           fontWeight: 'medium',
                           fontSize: '14px',
                           '&:hover': {
                              bgcolor: '#2563eb'
                           }
                        }}
                     >
                        Submit Feedback
                     </Button>
                  </Box>
               </>
            ) : (
               <>
                  {/* Success Header */}
                  <Box sx={{ 
                     display: 'flex', 
                     justifyContent: 'space-between', 
                     alignItems: 'center', 
                     p: 2, 
                     borderBottom: '1px solid #e5e7eb' 
                  }}>
                     <span></span>
                     <Button 
                        onClick={handleClose}
                        sx={{ 
                           color: '#9ca3af', 
                           minWidth: 'auto', 
                           p: 0.5,
                           fontSize: '20px',
                           '&:hover': { color: '#6b7280' }
                        }}
                     >
                        ✕
                     </Button>
                  </Box>

                  {/* Success Content */}
                  <Box sx={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     justifyContent: 'center', 
                     height: '320px', 
                     textAlign: 'center', 
                     p: 4 
                  }}>
                     <Box>
                        <Typography variant="h6" color="#374151" mb={1}>
                           Your response has been recorded.
                        </Typography>
                        <Typography variant="h6" color="#374151" fontWeight="medium">
                           Thank you!
                        </Typography>
                     </Box>
                  </Box>
               </>
            )}
         </Dialog>
      </>
   );
};

export default FeedbackWidget;